import { AgendaItemCategory } from './task';

export type MeetingSummaryType = {
  _id?: string;
  channelId: string;
  transcription: MeetingTranscription;

  processingTranscript?: boolean;
  summarizingTranscription?: SummarizingTranscriptionStatus | null;
  processingPercent?: number;
  isBeingProcessed?: boolean;

  recordingEnabled?: boolean;
  transcriptionEnabled?: boolean;
  minutesGenerationEnabled?: boolean;
  transcriptionsStatisticsEnabled?: boolean;

  transcriptionLanguage?: TranscriptionLanguage;
  topics?: TopicSummary[];
  protocol?: ProtocolSuggestion[];
  agendaItemSuggestions?: NewAgendaItemSuggestion[];

  speakerStats?: StatsItem[];
  agendaItemStats?: AgendaItemStats[];

  updatedAt: string;
};

export type AgendaItemStats = {
  agendaItemId?: string;
  suggestedTopic?: string;
  duration: number;
};
export type StatsItem = {
  speakerId: string;
  participantId: string;
  speakingTimePercentage: number;
  speakingTime: number;
  longestSpeechDuration: number;
  numQuestions: number;
  timesSpoken: number;
};

export enum ValidAudioFileTypes {
  MP3 = 'mpeg',
  WAV = 'wav',
  OGG = 'ogg',
  M4A = 'm4a',
}
export const ValidAudioFileTypesArray = ['mp4', 'm4a', 'ogg', 'wav', 'mpeg'] as const;

export type MeetingTranscription = {
  room_name?: string;
  start_time?: string;
  start_timestamp?: number;
  end_time?: string;

  initial_participants?: MeetingParticipant[];
  participants?: MeetingParticipant[];

  audioFileType?: ValidAudioFileTypes;
  audioFileId?: string;
  meetingSummaryIds?: string[]; // only used for merged transcriptions
  soundFileWithTimestampsId?: string;

  transcription?: TranscriptionText[];
};
export type MeetingEvent = {
  event?: string;
  timestamp?: number;
  transcript?: {
    confidence: number;
    text: string;
  }[];
  is_interim?: boolean;
  language?: string;
  message_id?: string;
  stability?: number;
  participant?: MeetingParticipant;
};
export type TopicSummary = {
  topic: string;
  bulletPoints: { text: string; start: number }[];
  context: string;
  summary: string;
  start: number;
  end: number;
  speakers: string[];
  questions: { title: string; participant: string; index: number }[];
  tasks: { title: string; description: string; index: number }[];
  decisions: { title: string; index: number }[];
  risks: { title: string; index: number }[];
  segments: AISummaryWithNewTopic[];
};
export type AISummary = {
  summary: string;
  start: number;
  end: number;
  speaker: string;
};
export type AISummaryWithNewTopic = AISummary & { newTopic: boolean; reasoning: string };

export type TranscriptionText = {
  start?: number;
  end?: number;
  text: string;
  words: TranscriptionWord[];
  participant_id?: string;
  speaker_id?: string;
  meetingSummaryId?: string; // only used for merged transcriptions
};
export type TranscriptionWord = {
  start?: number;
  end?: number;
  word: string;
  score?: number;
};
export type MeetingParticipant = {
  avatar_url?: string;
  name?: string;
  id?: string;
  email?: string;
};
export type TranscriptionChunk = {
  topic: string;
  from: number;
  to: number;
  state: 'DONE' | 'MORE';
  keywords: { line: number; keyword: string }[];
  timestamps?: { from: number; to: number }[];
  context?: string;
  summary?: string;
  participantSummaries?: {
    summary: string;
    speaker: string | null;
    start: number;
  }[];
  topicEnd: 'EXPLICIT' | 'IMPLICIT';
};
export type ProtocolSuggestion = {
  agendaItemIndex?: number;
  agendaItemId: string;
  longSummary?: string;
  shortSummary?: string;
  proposedResolution?: string;
  start: number;
  end: number;
};

export type NewAgendaItemSuggestion = {
  agendaItemId?: string;
  addedToAgenda?: boolean;
  topic: string;
  description: string;
  longSummary: string;
  shortSummary: string;
  agendaItemCategory: AgendaItemCategory;
  start: number;
  end: number;
  proposedResolution?: string;
};
export enum ProtocolLength {
  SHORT = 'SHORT',
  LONG = 'LONG',
}
export type TranscriptionLanguage = 'no' | 'en';

export enum SummarizingTranscriptionStatus {
  START_SUMMARIZING = 'START_SUMMARIZING',
  SUMMARIZING = 'SUMMARIZING',
  GENERATING_PROTOCOL = 'GENERATING_PROTOCOL',
}
export enum ProcessingTranscriptionStatus {
  START = 'START',
  SETUP = 'SETUP',
  PROCESSING = 'PROCESSING',
  FINISHED = 'FINISHED',
}
